import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { format, parseISO } from 'date-fns';
import { es, enUS, de as deLocale } from 'date-fns/locale';
import {
  Search,
  Plus,
  Phone,
  FileText,
  ChevronRight,
  Loader2,
  User,
  Archive,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';


const normalizeForSearch = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();


export const Clients = () => {
  const { api, language } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = language === 'es' ? es : language === 'de' ? deLocale : enUS;

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    surname: '',
    phone: '',
    internalNotes: '',
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/clients${search || includeArchived ? `?${search ? `search=${encodeURIComponent(search)}` : ''}${search && includeArchived ? '&' : ''}${includeArchived ? 'include_archived=true' : ''}` : ''}`);
      setClients(res.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      toast.error(t('success.clients.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, search, includeArchived, t]);

const archiveClient = async (e, clientId) => {
  e.stopPropagation();
  try {
    await api.delete(`/clients/${clientId}`);
    toast.success(t('success.clients.archived'));
    fetchClients();
  } catch (error) {
    const msg = error?.response?.data?.detail || 'Error al archivar cliente';
    toast.error(msg);
  }
};

const restoreClient = async (e, clientId) => {
  e.stopPropagation();
  try {
    await api.put(`/clients/${clientId}/restore`);
    toast.success(t('success.clients.restored'));
    fetchClients();
  } catch (error) {
    const msg = error?.response?.data?.detail || 'Error al restaurar cliente';
    toast.error(msg);
  }
};

const hardDeleteClient = async (e, clientId) => {
  e.stopPropagation();
  if (!window.confirm('¿Eliminar definitivamente este cliente? Esta acción no se puede deshacer.')) return;
  try {
    await api.delete(`/clients/${clientId}?hard=true`);
    toast.success(t('success.clients.deleted'));
    fetchClients();
  } catch (error) {
    const msg = error?.response?.data?.detail || 'No se puede eliminar: el cliente tiene reservas. Archiva el cliente.';
    toast.error(msg);
  }
};


  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreate = async () => {
    if (!formData.fullName.trim()) {
      toast.error(t('success.clients.nameRequired'));
      return;
    }

    setFormLoading(true);
    try {
      const res = await api.post('/clients', {
        ...formData,
        centerId: '', // Will be set by backend based on user
        active: true,
      });

      const createdClientId = res?.data?.id;
      toast.success(t('success.clients.created'));
      setShowDialog(false);
      setFormData({ fullName: '', phone: '', internalNotes: '' });

      if (createdClientId) {
        navigate(`/clients/${createdClientId}`);
        return;
      }

      fetchClients();
    } catch (error) {
      toast.error(t('success.clients.createError'));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 h-full flex flex-col" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-2xl font-bold tracking-tight uppercase">
          {t('clients.title')}
        </h1>
        
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
              className="pl-9 input-tactical"
              data-testid="client-search"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-zinc-400 select-none">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Mostrar archivados
          </label>
          
          <Button
            className="btn-primary"
            onClick={() => setShowDialog(true)}
            data-testid="add-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('clients.addClient')}
          </Button>
        </div>
      </div>

      {/* Clients list */}
      <div className="flex-1 min-h-0 overflow-y-auto">

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">
          {search ? t('common.noResults') : t('clients.noClients')}
        </div>
      ) : (
        <div className="grid gap-3">
          {
clients.slice().sort((a, b) => (a.fullName || '').localeCompare((b.fullName || ''), (language === 'es' ? 'es' : language === 'de' ? 'de' : 'en'), { sensitivity: 'base' })).map((client) => (
            <div
              key={client.id}
              className={`flex items-center gap-4 p-4 bg-card border rounded-sm transition-colors ${
                client.archived ? 'border-zinc-800 opacity-70' : 'border-zinc-800 hover:border-zinc-700'
              }`}
              data-testid={`client-row-${client.id}`}
            >
              <div
                onClick={() => navigate(`/clients/${client.id}`)}
                className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-zinc-400" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {[client.fullName, client.surname].filter(Boolean).join(' ')}
                  </div>
                  {client.phone && (
                    <div className="flex items-center gap-1 text-sm text-zinc-500">
                      <Phone className="w-3 h-3" />
                      {client.phone}
                    </div>
                  )}
                </div>

                <ChevronRight className="w-5 h-5 text-zinc-600" />
              </div>

              <div className="flex items-center gap-2">
                {!client.archived ? (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => archiveClient(e, client.id)}
                      title="Archivar"
                      data-testid={`archive-client-${client.id}`}
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={(e) => hardDeleteClient(e, client.id)}
                      title="Eliminar definitivamente"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => restoreClient(e, client.id)}
                      title="Restaurar"
                      data-testid={`restore-client-${client.id}`}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={(e) => hardDeleteClient(e, client.id)}
                      title="Eliminar definitivamente"
                      data-testid={`delete-client-${client.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
{/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide">
              {t('clients.addClient')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('clients.fullName')} *</Label>
              <Input
                value={formData.fullName}
                onChange={(e) => setFormData(f => ({ ...f, fullName: e.target.value }))}
                className="input-tactical"
                data-testid="client-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('clients.surname')}</Label>
              <Input
                value={formData.surname}
                onChange={e => setFormData({...formData, surname: e.target.value})}
                placeholder={t('clients.surname')}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('clients.phone')}</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(f => ({ ...f, phone: e.target.value }))}
                placeholder="+34 612 345 678"
                className="input-tactical"
                data-testid="client-phone-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('clients.notes')}</Label>
              <Textarea
                value={formData.internalNotes}
                onChange={(e) => setFormData(f => ({ ...f, internalNotes: e.target.value }))}
                className="input-tactical"
                rows={3}
                data-testid="client-notes-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                className="btn-primary"
                onClick={handleCreate}
                disabled={formLoading}
                data-testid="save-client-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.create')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
